import React, { useEffect, useRef } from 'react';
import {
  CheckCircle, XCircle, Loader2, Terminal,
  Wifi, GitBranch, Trash2, Package, Zap, Copy, RefreshCw,
  ChevronDown, ChevronUp, Play,
} from 'lucide-react';
import type { CommitInfo, DeployStatus, LogEntry, ServerStatus } from './types';

const PIPELINE_STEPS = [
  { id: 'connect', label: 'SSH Connect', icon: <Wifi size={15} />, startPattern: /Connecting to/i, donePattern: /Connected to|OS:/i },
  { id: 'git', label: 'Git sync', icon: <GitBranch size={15} />, startPattern: /Git: fetching|Git: checkout|Git: pull origin|\$ .*git pull|Running git pull|Cloning .+→/i, donePattern: /Git sync complete|Git clone finished|Skipping git pull|Git pull complete/i },
  { id: 'clean', label: 'Clean dist', icon: <Trash2 size={15} />, startPattern: /Cleaning old build/i, donePattern: /Old build folder removed/i },
  { id: 'install', label: 'npm install', icon: <Package size={15} />, startPattern: /Installing dependencies/i, donePattern: /Dependencies installed/i },
  { id: 'build', label: 'npm build', icon: <Zap size={15} />, startPattern: /Building .+\.\.\./i, donePattern: /Build complete/i },
  { id: 'deploy', label: 'Copy files', icon: <Copy size={15} />, startPattern: /Deploying to|Clearing deploy directory|Deploy directory|Clearing current deploy/i, donePattern: /Files copied to|Files restored to/i },
  { id: 'reload', label: 'Reload nginx', icon: <RefreshCw size={15} />, startPattern: /reload nginx|reload apache/i, donePattern: /Nginx reloaded|Apache reloaded/i },
];

type StepState = 'pending' | 'running' | 'done' | 'error';

function getPipelineStates(logs: { message: string }[], status: DeployStatus, serverCount = 1): StepState[] {
  const msgs = logs.map((l) => l.message);
  const states = PIPELINE_STEPS.map((): StepState => 'pending');
  for (let i = 0; i < PIPELINE_STEPS.length; i++) {
    const s = PIPELINE_STEPS[i];
    const doneCount = msgs.filter((m) => s.donePattern.test(m)).length;
    if (doneCount >= serverCount) states[i] = 'done';
    else if (msgs.some((m) => s.startPattern.test(m))) states[i] = status === 'error' ? 'error' : 'running';
  }
  if (status === 'error') {
    const last = states.lastIndexOf('running');
    if (last !== -1) states[last] = 'error';
  }
  return states;
}

const ROLLBACK_IDS = ['connect', 'deploy', 'reload'];

export function LivePipeline({
  logs,
  status,
  label,
  mode = 'deploy',
  serverCount = 1,
}: {
  logs: { message: string }[];
  status: DeployStatus;
  label: string;
  mode?: 'deploy' | 'rollback';
  serverCount?: number;
}) {
  if (status === 'idle') return null;
  const allStates = getPipelineStates(logs, status, serverCount);
  const steps = mode === 'rollback' ? PIPELINE_STEPS.filter((s) => ROLLBACK_IDS.includes(s.id)) : PIPELINE_STEPS;
  const states = mode === 'rollback' ? allStates.filter((_, i) => ROLLBACK_IDS.includes(PIPELINE_STEPS[i].id)) : allStates;

  return (
    <div className="live-pipeline">
      <div className="lp-label-row">
        {status === 'running' && <Loader2 size={12} className="spin" style={{ color: 'var(--yellow)' }} />}
        {status === 'success' && <CheckCircle size={12} style={{ color: 'var(--green)' }} />}
        {status === 'error' && <XCircle size={12} style={{ color: 'var(--red)' }} />}
        <span>{label}</span>
      </div>
      <div className="lp-steps">
        {steps.map((step, i) => {
          const st = states[i];
          return (
            <React.Fragment key={step.id}>
              <div className={`lp-step lp-${st}`}>
                <div className="lp-icon">
                  {st === 'running' ? <Loader2 size={14} className="spin" /> :
                   st === 'done' ? <CheckCircle size={14} /> :
                   st === 'error' ? <XCircle size={14} /> : step.icon}
                </div>
                <span className="lp-name">{step.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`lp-line ${st === 'done' ? 'done' : ''}`} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export function CommitModal({
  commits,
  selected,
  onSelect,
  onClose,
}: {
  commits: CommitInfo[];
  selected: string;
  onSelect: (hash: string) => void;
  onClose: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="cm-overlay"
      ref={overlayRef}
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="cm-dialog">
        <div className="cm-header">
          <span className="cm-title">
            <GitBranch size={14} /> Select Commit
          </span>
          <button type="button" className="cm-close" onClick={onClose}>
            <XCircle size={16} />
          </button>
        </div>
        <div className="cm-list">
          <div
            role="button"
            tabIndex={0}
            className={`cm-item cm-latest ${selected === '' ? 'cm-selected' : ''}`}
            onClick={() => {
              onSelect('');
              onClose();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onSelect('');
                onClose();
              }
            }}
          >
            <span className="cm-sha">HEAD</span>
            <span className="cm-msg">Latest (HEAD) — most recent commit on branch</span>
            {selected === '' && <CheckCircle size={13} className="cm-check" />}
          </div>
          {commits.map((c) => (
            <div
              role="button"
              tabIndex={0}
              key={c.hash}
              className={`cm-item ${selected === c.hash ? 'cm-selected' : ''}`}
              onClick={() => {
                onSelect(c.hash);
                onClose();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onSelect(c.hash);
                  onClose();
                }
              }}
            >
              <span className="cm-sha">{c.short}</span>
              <span className="cm-msg">{c.subject}</span>
              <span className="cm-meta">
                {c.author} · {c.date}
              </span>
              {selected === c.hash && <CheckCircle size={13} className="cm-check" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function LogPanel({
  logs,
  open,
  onToggle,
  title = 'Logs',
}: {
  logs: LogEntry[];
  open: boolean;
  onToggle: () => void;
  title?: string;
}) {
  if (!logs.length) return null;

  return (
    <div className="log-panel">
      <div className={`log-header ${open ? 'open' : ''}`} onClick={onToggle}>
        <div className="log-title">
          <Terminal size={12} />
          <span>{title}</span>
          <span className="log-count">{logs.length}</span>
        </div>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </div>
      {open && (
        <div className="log-body">
          {logs.map((l, i) => {
            const isErr = /error|failed|fatal|FAILED/i.test(l.message);
            const isOk = /success|complete|deployed|reloaded|verified|found/i.test(l.message);
            const isCmd = l.message.startsWith('$');
            const isHead = l.message.startsWith('━');
            const isWarn = l.message.startsWith('  (warn)');
            return (
              <div
                key={i}
                className={`log-line ${isErr ? 'log-err' : isOk ? 'log-ok' : isCmd ? 'log-cmd' : isHead ? 'log-head' : isWarn ? 'log-warn' : ''}`}
              >
                <span className="log-ts">{new Date(l.time).toLocaleTimeString()}</span>
                <span className="log-msg">{l.message}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function DeployBtn({
  label,
  onClick,
  status,
  disabled,
  variant = 'primary',
}: {
  label: string;
  onClick: () => void;
  status: DeployStatus;
  disabled?: boolean;
  variant?: 'primary' | 'lg';
}) {
  return (
    <button
      type="button"
      className={`btn btn-deploy ${variant === 'lg' ? 'btn-deploy-lg' : ''}`}
      onClick={onClick}
      disabled={disabled || status === 'running'}
    >
      {status === 'running' ? <Loader2 size={13} className="spin" /> : <Play size={13} />}
      <span>{status === 'running' ? 'Deploying…' : label}</span>
      {status === 'success' && <CheckCircle size={12} className="btn-status-icon ok" />}
      {status === 'error' && <XCircle size={12} className="btn-status-icon err" />}
    </button>
  );
}

export const STATUS_COLOR: Record<ServerStatus, string> = {
  unknown: 'var(--text-3)',
  testing: 'var(--yellow)',
  online: 'var(--green)',
  offline: 'var(--red)',
};

export function serverFlag(label: string): string {
  const l = label.toLowerCase();
  if (l.includes('india') || l.includes('cad') || l.includes('in')) return '🇮🇳';
  if (l.includes('us') || l.includes('usa') || l.includes('america')) return '🇺🇸';
  return '🖥';
}
